import React, { createContext, useState, ReactNode, useMemo, useEffect, useRef } from 'react';
import { useRouter } from 'next/router';
import { useTheme } from '@rbx/ui';
import { OpenAPI } from '@rbx/docsite-utilities/dist/browser';
import { OpenAPIPageContent } from '@modules/utilities/pageBuild/content';
import unifiedLoggerClient from '@modules/eventStream/implementations/unifiedLoggerClient';
import DocsSiteEventType from '@modules/eventStream/enum/DocsSiteEventType';
import useOpenAPIDocument from '../hooks/useOpenAPIDocument';
import {
  FilterOption,
  FilterOptionType,
  getFilteredOperations,
  getAvailableFilterOptions,
  filterOptionsToQueryParams,
  queryParamsToFilterOptions,
} from '../utils/filterUtils';

export interface FilteredOperationsContextValue {
  filteredOperations: Array<{
    operation: OpenAPI.OperationObject;
    method: OpenAPI.HttpMethod;
    path: string;
  }>;
  filterOptions: FilterOption[];
  selectedFilterOptions: FilterOption[];
  setSelectedFilterOptions: (newValue: FilterOption[]) => void;
}

export interface FilteredOperationsProviderProps {
  children: ReactNode;
  content: OpenAPIPageContent;
}

export const FilteredOperationsContext = createContext<FilteredOperationsContextValue | null>(null);

export const FilteredOperationsProvider: React.FC<FilteredOperationsProviderProps> = ({
  children,
  content,
}) => {
  const document = useOpenAPIDocument();
  const theme = useTheme();
  const router = useRouter();

  const [selectedFilterOptions, setSelectedFilterOptions] = useState<FilterOption[]>([]);
  const [isInitialized, setIsInitialized] = useState(false);
  const previousFilterOptionsRef = useRef<FilterOption[]>([]);

  const filteredOperations = useMemo(
    () => getFilteredOperations(document, content, selectedFilterOptions),
    [document, content, selectedFilterOptions],
  );

  const filterOptions = useMemo(
    () => getAvailableFilterOptions(document, theme),
    [document, theme],
  );

  // Read initial filter state from URL on mount
  useEffect(() => {
    if (isInitialized || filterOptions.length === 0) {
      return;
    }

    const hasAnyFilters = Object.values(FilterOptionType).some(
      (filterType) => router.query[filterType] !== undefined,
    );
    if (hasAnyFilters) {
      const deserialized = queryParamsToFilterOptions(router.query, filterOptions);
      setSelectedFilterOptions(deserialized);
    }
    setIsInitialized(true);
  }, [router.query, filterOptions, isInitialized]);

  // Log filter changes
  useEffect(() => {
    if (!isInitialized || filterOptions.length === 0) {
      return;
    }

    const filterOptionToKey = (option: FilterOption): string => `${option.type}:${option.value}`;
    const previousSet = new Set(previousFilterOptionsRef.current.map(filterOptionToKey));
    const selectedSet = new Set(selectedFilterOptions.map(filterOptionToKey));
    if (
      previousSet.size === selectedSet.size &&
      Array.from(previousSet).every((key) => selectedSet.has(key))
    ) {
      return;
    }

    const serializedFilters = filterOptionsToQueryParams(selectedFilterOptions);

    unifiedLoggerClient.logClickEvent({
      eventName: DocsSiteEventType.OpenCloudFilterChanged,
      parameters: {
        authFilters: serializedFilters[FilterOptionType.Auth] || '',
        stabilityFilters: serializedFilters[FilterOptionType.Stability] || '',
        domainFilters: serializedFilters[FilterOptionType.Domain] || '',
        totalFilters: selectedFilterOptions.length.toString(),
      },
    });

    previousFilterOptionsRef.current = selectedFilterOptions;
  }, [selectedFilterOptions, isInitialized, filterOptions, router]);

  // Sync filter changes to URL
  useEffect(() => {
    if (!isInitialized || filterOptions.length === 0) {
      return;
    }

    const currentFilterOptions = queryParamsToFilterOptions(router.query, filterOptions);
    const filterOptionToKey = (option: FilterOption): string => `${option.type}:${option.value}`;
    const currentSet = new Set(currentFilterOptions.map(filterOptionToKey));
    const selectedSet = new Set(selectedFilterOptions.map(filterOptionToKey));

    if (
      currentSet.size === selectedSet.size &&
      Array.from(currentSet).every((key) => selectedSet.has(key))
    ) {
      return;
    }

    router.replace(
      {
        pathname: router.asPath.split('?')[0],
        query: filterOptionsToQueryParams(selectedFilterOptions),
      },
      undefined,
      { shallow: true },
    );
  }, [selectedFilterOptions, isInitialized, router, filterOptions]);

  const contextValue: FilteredOperationsContextValue = useMemo(
    () => ({
      filteredOperations,
      filterOptions,
      selectedFilterOptions,
      setSelectedFilterOptions,
    }),
    [filteredOperations, filterOptions, selectedFilterOptions, setSelectedFilterOptions],
  );

  return (
    <FilteredOperationsContext.Provider value={contextValue}>
      {children}
    </FilteredOperationsContext.Provider>
  );
};

export default FilteredOperationsProvider;
